import { GuideSection } from '@/components/guides/GuideSection';
import { GuideCallout } from '@/components/guides/GuideCallout';

const ISSUES = [
  {
    symptom: '"Microphone permission denied" at launch',
    fix: 'Open System Settings → Privacy & Security → Microphone (macOS) or Settings → Privacy → Microphone (Windows). Enable NeuraX. Restart the app.',
  },
  {
    symptom: "The interviewer's voice isn't showing up in the transcript",
    fix: 'NeuraX needs system audio / screen recording permission to hear their side. On macOS: System Settings → Privacy & Security → Screen & System Audio Recording → enable NeuraX → restart it. On Windows 11: Settings → Privacy & security → Microphone → enable NeuraX.',
  },
  {
    symptom: '"License inactive" or "Could not activate license"',
    fix: 'Your license may already be bound to a different laptop, or it may have been revoked. Contact your recruiter. They can reset machine binding or issue a fresh license.',
  },
  {
    symptom: "The app says 'No scheduled session'",
    fix: "Your recruiter hasn't scheduled an interview session for you yet. Check with them. The app updates automatically within a few seconds of a session being scheduled.",
  },
  {
    symptom: 'NeuraX crashed during the interview',
    fix: 'Relaunch the app and click Start session again — it will pick up the existing session. If the transcript is incomplete, tell your recruiter; they can file a refund for the broken session.',
  },
  {
    symptom: 'The company code is rejected',
    fix: "Double-check the code (case-insensitive, but hyphens matter). If it still fails, your team's code may have hit its registration cap. Ask your recruiter to confirm or request a new code.",
  },
  {
    symptom: 'I entered the wrong email on first launch',
    fix: "Contact your recruiter. They can clear your registration and you'll re-enter the app with the correct email.",
  },
  {
    symptom: 'Coaching pane is empty during the interview',
    fix: "Check the left transcript pane — if it's empty too, audio isn't being captured. Fix the mic/system-audio permissions (see the first two rows). If the transcript is populating but the coaching pane is blank, try the 'Regenerate' button at the top of the pane.",
  },
];

export function TroubleshootingSection() {
  return (
    <GuideSection
      id="troubleshooting"
      eyebrow="Something broke?"
      title="Troubleshooting"
      description="The common failure modes and how to get unstuck fast. When in doubt, contact your recruiter — they can fix most issues in a minute."
    >
      <div className="overflow-hidden rounded-2xl border border-white/5">
        <table className="w-full text-sm">
          <thead className="bg-white/5 text-xs uppercase tracking-wider text-muted-foreground">
            <tr>
              <th className="px-4 py-3 text-left font-medium">What you see</th>
              <th className="px-4 py-3 text-left font-medium">How to fix it</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-white/5">
            {ISSUES.map((issue) => (
              <tr key={issue.symptom} className="align-top">
                <td className="px-4 py-3 font-medium text-white">{issue.symptom}</td>
                <td className="px-4 py-3 text-muted-foreground">{issue.fix}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <GuideCallout variant="info" title="When to contact your recruiter">
        <p>
          For anything license-related, session-related, or billing-related, your recruiter is the
          right first contact. They have admin access and can resolve most problems without
          involving platform support.
        </p>
      </GuideCallout>

      <GuideCallout variant="tip" title="Still stuck?">
        <p>
          If your recruiter can't help, they'll escalate to platform support on your behalf. Include
          in your message: your license key, the company code, the laptop OS/version, and a brief
          description of what happened. The more context, the faster the fix.
        </p>
      </GuideCallout>
    </GuideSection>
  );
}
